import React, { useEffect, useMemo, useReducer, useRef } from 'react'
import {
  clearInvite,
  getStoredInviteEmail,
  getStoredInviteToken,
  persistInvite,
} from 'lib/utils/coupleInvite'
import { useSearchParams } from 'react-router-dom'

const APP_LAUNCH_TIMEOUT = 1500
const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || ''
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || ''

type BridgeState = 'launching' | 'fallback' | 'missing-token'

type State = {
  status: BridgeState | 'handoff-complete'
  launchAttempted: boolean
}

type Action =
  | { type: 'TOKEN_MISSING' }
  | { type: 'LAUNCH_REQUESTED' }
  | { type: 'LAUNCH_FAILED' }
  | { type: 'HANDOFF_COMPLETED' }

const initialState: State = {
  status: 'launching',
  launchAttempted: false,
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'TOKEN_MISSING':
      return { ...state, status: 'missing-token' }
    case 'LAUNCH_REQUESTED':
      return { status: 'launching', launchAttempted: true }
    case 'LAUNCH_FAILED':
      return { ...state, status: 'fallback' }
    case 'HANDOFF_COMPLETED':
      return { ...state, status: 'handoff-complete' }
    default:
      return state
  }
}

const getPlatform = (): 'ios' | 'android' | 'desktop' => {
  const ua = navigator.userAgent
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios'
  if (/Android/.test(ua)) return 'android'
  return 'desktop'
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const AppIcon: React.FC = () => (
  <div
    style={{
      width: 80,
      height: 80,
      borderRadius: 20,
      background: 'linear-gradient(135deg, #D6FF00 0%, #a8cc00 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 8px 32px rgba(214,255,0,0.25)',
      margin: '0 auto',
      flexShrink: 0,
    }}
  >
    <svg width="42" height="42" viewBox="0 0 24 24" fill="none">
      <path
        d="M8 12l3 3 5-5"
        stroke="#000"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
)

const Spinner: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0' }}>
    <div
      style={{
        width: 28,
        height: 28,
        border: '2.5px solid #2a2a2a',
        borderTop: '2.5px solid #D6FF00',
        borderRadius: '50%',
        animation: 'ci-spin 0.8s linear infinite',
      }}
    />
    <style>{`@keyframes ci-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
)

const AppleIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
)

const AndroidIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zm-2.5-1C2.67 17 2 17.67 2 18.5v-9C2 8.67 2.67 8 3.5 8S5 8.67 5 9.5v9c0 .83-.67 1.5-1.5 1.5zm17 0c-.83 0-1.5-.67-1.5-1.5v-9c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v9c0 .83-.67 1.5-1.5 1.5zM15.53 2.16l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48A5.962 5.962 0 0 0 12 1c-.96 0-1.86.23-2.66.63L7.88.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31A5.983 5.983 0 0 0 6 7h12a5.983 5.983 0 0 0-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z" />
  </svg>
)

interface StoreButtonProps {
  href: string
  label: string
  icon: React.ReactNode
}

const StoreButton: React.FC<StoreButtonProps> = ({ href, label, icon }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      height: 52,
      borderRadius: 14,
      background: '#111',
      border: '1px solid #2a2a2a',
      color: '#fff',
      fontWeight: 600,
      fontSize: 15,
      textDecoration: 'none',
      transition: 'background 0.15s, border-color 0.15s',
    }}
    onMouseEnter={(e) => {
      const el = e.currentTarget as HTMLAnchorElement
      el.style.background = '#1a1a1a'
      el.style.borderColor = '#3a3a3a'
    }}
    onMouseLeave={(e) => {
      const el = e.currentTarget as HTMLAnchorElement
      el.style.background = '#111'
      el.style.borderColor = '#2a2a2a'
    }}
  >
    {icon}
    {label}
  </a>
)

// ─── Main component ───────────────────────────────────────────────────────────

const CoupleInvite: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [state, dispatch] = useReducer(reducer, initialState)
  const launchTimeoutRef = useRef<number | null>(null)
  const pageHiddenRef = useRef(false)

  const tokenFromUrl = searchParams.get('token')
  const emailFromUrl = searchParams.get('email')
  const storedToken = getStoredInviteToken()
  const storedEmail = getStoredInviteEmail()
  const token = tokenFromUrl || storedToken
  const email = emailFromUrl || storedEmail || ''
  const platform = getPlatform()

  const deepLinkUrl = useMemo(() => {
    if (!token) return ''
    const params = new URLSearchParams({ token })
    if (email) params.set('email', email)
    return `texttonext://couple-invite?${params.toString()}`
  }, [token, email])

  const syncUrl = () => {
    if (!token) return
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('token', token)
    if (email) nextParams.set('email', email)
    setSearchParams(nextParams, { replace: true })
  }

  const tryOpenApp = () => {
    if (!deepLinkUrl) {
      dispatch({ type: 'TOKEN_MISSING' })
      return
    }
    if (launchTimeoutRef.current) window.clearTimeout(launchTimeoutRef.current)
    pageHiddenRef.current = false
    dispatch({ type: 'LAUNCH_REQUESTED' })
    launchTimeoutRef.current = window.setTimeout(() => {
      if (!pageHiddenRef.current) dispatch({ type: 'LAUNCH_FAILED' })
    }, APP_LAUNCH_TIMEOUT)
    window.location.href = deepLinkUrl
  }

  useEffect(() => {
    if (!token) {
      clearInvite()
      dispatch({ type: 'TOKEN_MISSING' })
      return
    }
    persistInvite(token, email)
    syncUrl()
    tryOpenApp()
    // eslint-disable-next-line
  }, [tokenFromUrl, emailFromUrl])

  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) return
      pageHiddenRef.current = true
      if (launchTimeoutRef.current)
        window.clearTimeout(launchTimeoutRef.current)
      dispatch({ type: 'HANDOFF_COMPLETED' })
    }
    const handlePageHide = () => {
      pageHiddenRef.current = true
      if (launchTimeoutRef.current)
        window.clearTimeout(launchTimeoutRef.current)
      dispatch({ type: 'HANDOFF_COMPLETED' })
    }
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('pagehide', handlePageHide)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('pagehide', handlePageHide)
      if (launchTimeoutRef.current)
        window.clearTimeout(launchTimeoutRef.current)
    }
  }, [])

  const renderStoreButtons = () => {
    if (platform === 'ios' && APP_STORE_URL) {
      return (
        <StoreButton
          href={APP_STORE_URL}
          label="App Store'dan İndir"
          icon={<AppleIcon />}
        />
      )
    }
    if (platform === 'android' && PLAY_STORE_URL) {
      return (
        <StoreButton
          href={PLAY_STORE_URL}
          label="Google Play'den İndir"
          icon={<AndroidIcon />}
        />
      )
    }
    return (
      <>
        {APP_STORE_URL && (
          <StoreButton
            href={APP_STORE_URL}
            label="App Store"
            icon={<AppleIcon />}
          />
        )}
        {PLAY_STORE_URL && (
          <StoreButton
            href={PLAY_STORE_URL}
            label="Google Play"
            icon={<AndroidIcon />}
          />
        )}
      </>
    )
  }

  const hasStoreLink = APP_STORE_URL || PLAY_STORE_URL

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: 'radial-gradient(ellipse at top, #0d1a00 0%, #030303 60%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: 'rgba(10,10,10,0.96)',
          border: '1px solid #1e1e1e',
          borderRadius: 28,
          padding: '32px 28px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 32px 80px rgba(0,0,0,0.7)',
        }}
      >
        <AppIcon />

        {/* ── LAUNCHING ───────────────────────────────────────── */}
        {state.status === 'launching' && (
          <div
            style={{
              marginTop: 28,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              textAlign: 'center',
            }}
          >
            <Spinner />
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                color: '#fff',
              }}
            >
              Uygulama Açılıyor…
            </h1>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                color: '#888',
                lineHeight: 1.5,
              }}
            >
              TextToNext uygulamasına yönlendiriliyorsunuz.
            </p>
            {email && (
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  color: '#555',
                  background: '#111',
                  borderRadius: 10,
                  padding: '8px 12px',
                }}
              >
                {email}
              </p>
            )}
          </div>
        )}

        {/* ── FALLBACK ────────────────────────────────────────── */}
        {state.status === 'fallback' && (
          <>
            <div
              style={{
                marginTop: 28,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <h1
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 700,
                  color: '#fff',
                }}
              >
                Uygulama Açılamadı
              </h1>
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  color: '#888',
                  lineHeight: 1.6,
                }}
              >
                {platform !== 'desktop'
                  ? 'TextToNext uygulaması cihazınızda yüklü değil gibi görünüyor. Aşağıdan indirin ve ardından tekrar deneyin.'
                  : 'Uygulamayı açmak için mobil cihazınızdan bu linki açın veya uygulamayı yükleyin.'}
              </p>
              {email && (
                <p
                  style={{
                    margin: '4px 0 0',
                    fontSize: 13,
                    color: '#666',
                    background: '#111',
                    borderRadius: 10,
                    padding: '8px 14px',
                  }}
                >
                  Davet: <strong style={{ color: '#aaa' }}>{email}</strong>
                </p>
              )}
            </div>

            <div
              style={{
                marginTop: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <button
                onClick={tryOpenApp}
                style={{
                  height: 52,
                  borderRadius: 14,
                  background: '#D6FF00',
                  color: '#000',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: 'pointer',
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.85')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                Tekrar Dene
              </button>

              {hasStoreLink && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      margin: '4px 0',
                    }}
                  >
                    <div
                      style={{ flex: 1, height: 1, background: '#1e1e1e' }}
                    />
                    <span style={{ fontSize: 12, color: '#444' }}>veya</span>
                    <div
                      style={{ flex: 1, height: 1, background: '#1e1e1e' }}
                    />
                  </div>
                  {renderStoreButtons()}
                </>
              )}
            </div>

            <p
              style={{
                margin: '20px 0 0',
                fontSize: 12,
                color: '#3d3d3d',
                textAlign: 'center',
                lineHeight: 1.6,
              }}
            >
              Davet bağlantısı cihazınıza kaydedildi.
              <br />
              Uygulamayı indirdikten sonra bu sayfayı tekrar açın.
            </p>
          </>
        )}

        {/* ── MISSING TOKEN ───────────────────────────────────── */}
        {state.status === 'missing-token' && (
          <div
            style={{
              marginTop: 28,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                color: '#fff',
              }}
            >
              Geçersiz Davet
            </h1>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                color: '#888',
                lineHeight: 1.6,
              }}
            >
              Davet bağlantısı eksik veya hatalı.
              <br />
              Lütfen e-postadaki linki tekrar açın.
            </p>
            <div
              style={{
                marginTop: 8,
                padding: '14px 16px',
                borderRadius: 14,
                background: 'rgba(255,80,80,0.07)',
                border: '1px solid rgba(255,80,80,0.15)',
              }}
            >
              <span style={{ fontSize: 13, color: '#f87171' }}>
                Token bulunamadı
              </span>
            </div>
          </div>
        )}

        {/* ── HANDOFF COMPLETE ────────────────────────────────── */}
        {state.status === 'handoff-complete' && (
          <div
            style={{
              marginTop: 28,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div style={{ fontSize: 40 }}>🎉</div>
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                color: '#fff',
              }}
            >
              Uygulama Açıldı!
            </h1>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                color: '#888',
                lineHeight: 1.6,
              }}
            >
              TextToNext geçişi algılandı.
              <br />
              Akış uygulama içinde devam edecek.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default CoupleInvite
